/* eslint-disable @typescript-eslint/no-unused-vars */
// MenuData.ts
import {
  isCarrierAccessEnabled,
  isExtenderPopEnabled,
  isInsightTopologyV2Enabled,
} from "@shared/config/featureFlags";
import { ReactComponent as HomeSvg } from "@assets/Icons/Icon.svg";
import { ReactComponent as IconReflector } from "@assets/Icons/IconReflector.svg";

// Kontrol menu icons
import { ReactComponent as ServerSVG } from "@assets/Icons/IconServer.svg";
import { ReactComponent as BackboneSVG } from "@assets/Icons/IconBackbone.svg";
import { ReactComponent as ConnectivityKontrolSVG } from "@assets/Icons/IconConnectivityKontrol.svg";
import { ReactComponent as DataInterconnectSVG } from "@assets/Icons/IconDataInterconnect.svg";
import { ReactComponent as InternetSVG } from "@assets/Icons/IconInternet.svg";
import { ReactComponent as IconFirewall } from "@assets/Icons/IconFwaas.svg";
import { ReactComponent as IconSdwan } from "@assets/Icons/Icon_sdwan.svg";
import { ReactComponent as IconBuilding } from "@assets/Icons/IconBuilding.svg";
import { ReactComponent as IconAccess } from "@assets/Icons/Icon_Wifi.svg";
import { ReactComponent as IconWarning } from "@assets/Icons/Icon_Warning.svg";

// Settings icons
// import { ReactComponent as BillingSVG } from "@assets/Icons/IconBilling.svg";
import { ReactComponent as LogsSVG } from "@assets/Icons/IconLogs.svg";
import { ReactComponent as SupportSVG } from "@assets/Icons/IconSupport.svg";
import { ReactComponent as Quotation } from "@assets/Icons/IconQuotation.svg";
import { ReactComponent as ReportSVG } from "@assets/Icons/IconReport.svg";
import { ReactComponent as IconKey } from "@assets/Icons/IconKey.svg";
import { ReactComponent as IconLicense } from "@assets/Icons/Icon_FileSign.svg";
import { ReactComponent as IconEyeSmall } from "@assets/Icons/SmallEye.svg";
import { ReactComponent as IconProfile2User } from "@assets/Icons/profile-2user.svg";
import { ReactComponent as IconPeople } from "@assets/Icons/people.svg";
import { ReactComponent as IconTag } from "@assets/Icons/IconTag.svg";
import { ReactComponent as IconNotifications } from "@assets/Icons/IconNotifications.svg";
import { ReactComponent as ProfileCircle } from "@assets/profile-circle.svg";

// === CTX Reflector V2 Menu Items ===
// import { settingsItems } from "./MenuData";

export interface MenuItem {
  label: string;
  id?: string;
  path?: string;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  children?: MenuItem[];
  badge?: string;
  /**
   * Static label (e.g. prototype) shown next to the row badge; not replaced by
   * dynamic count.
   *
   * **Only for states that have no expiry date** — `Beta` lasts as long as the
   * feature is in beta, and someone deleting it is a deliberate act. A `New`
   * does not belong here: it is a claim with a shelf life, and this field has
   * no clock, so it stays lit until a person remembers. That is exactly what
   * happened to Network Topology — GA on 2026-08-06, still announcing itself
   * as new five weeks later (CTX-502).
   *
   * `New` is derived instead, from the entry's `releasedAt` and its
   * `walkthrough.path`, and it expires on its own — see
   * `useChangelogNewItems.ts` (CTX-511). `MenuData.badges.test.ts` keeps this
   * field from growing another one.
   */
  badgeBeta?: string;
  customAction?: () => void;
}

export const mainItems: MenuItem[] = [
  { label: "Home", id: "home", path: "/", Icon: HomeSvg },
];

/** Kontrol Edge — shown under Main (below Overview), not under Services. */
export const reportsMenuItem: MenuItem = {
  label: "Reports",
  id: "reports-root",
  Icon: ReportSVG,
  path: "/kontrol/reports",
};

/** Kontrol Edge — "Events & Reports" section items. */
export const eventManagementItems: MenuItem[] = [
  {
    label: "Reports",
    id: "reports-root",
    Icon: ReportSVG,
    path: "/kontrol/reports",
  },
  {
    label: "Alerts",
    id: "kontrol-alerts",
    Icon: IconWarning,
    path: "/kontrol/alerts",
    badgeBeta: "Beta",
  },
  {
    label: "Internet monitoring",
    id: "kontrol-internet-ip-monitoring",
    Icon: InternetSVG,
    path: "/kontrol/internet-ip-monitoring",
    badgeBeta: "Beta",
  },
];

/**
 * Kontrol Edge services, grouped by connectivity layer (CTX-660).
 *
 * Each layer is a **section** — `children`, no `path` — because the Kontrol
 * branch of `SideMenuNavByProduct` renders one `MenuSection` per row here, the
 * same shape `Management` uses in `reflectorV2MenuItems`. A layer modelled as a
 * `MenuItem` parent instead would disappear from the collapsed rail: a collapsed
 * parent paints only its `Icon` and never mounts its children, and a layer has
 * no icon of its own.
 *
 * Order is the catalogue's own — L1 upward, then the services that sit on top of
 * all three layers rather than inside one.
 *
 * Carrier Access sits in L1 (P3, CTX-651), behind its BETA flag. Carrier
 * Bridge (L2) is still deliberately absent: it has no route, view or backend
 * yet, and the grouping leaves the shape it slots into instead of rendering a
 * row that leads nowhere.
 *
 * CTX Hub and CTX Reflector stay outside the layers, where they have always
 * been. Neither carries a `/kontrol` path, so `getFilteredServiceItems` keeps
 * them out of the Kontrol rail and they reach the menu through their own
 * products.
 */
export const serviceItems: MenuItem[] = [
  {
    label: "CTX Hub",
    id: "ctx-hub",
    children: [
      { label: "Data Usage", id: "ctx-hub-status", path: "/ctxhub/status" },
    ],
  },
  {
    label: "L1 (physical)",
    id: "kontrol-layer-l1",
    children: [
      {
        label: "Ports",
        id: "kontrol-port",
        path: "/kontrol/port",
        Icon: ServerSVG,
      },
      // Extended PoP extends the PoP into the customer site, so it is physical
      // access like Ports (CTX-362 had it close the flat Services list; the
      // layer is where it belongs now). BETA: hidden in production via
      // isExtenderPopEnabled(), which also gates its routes.
      ...(isExtenderPopEnabled()
        ? [
            {
              label: "Extended PoP",
              id: "kontrol-extender-pop",
              path: "/kontrol/extender-pop",
              Icon: IconBuilding,
            },
          ]
        : []),
      // Carrier Access is the last mile that joins the customer edge to the
      // backbone (P3), so it is physical access too. BETA: hidden in
      // production via isCarrierAccessEnabled(), which also gates its routes.
      ...(isCarrierAccessEnabled()
        ? [
            {
              label: "Carrier Access",
              id: "kontrol-carrier-access",
              path: "/kontrol/carrier-access",
              Icon: IconAccess,
            },
          ]
        : []),
    ],
  },
  {
    label: "L2",
    id: "kontrol-layer-l2",
    children: [
      {
        label: "DC interconnect",
        id: "kontrol-dc",
        path: "/kontrol/datacenter",
        Icon: DataInterconnectSVG,
      },
      {
        label: "Cloud connectivity",
        id: "kontrol-cc",
        path: "/kontrol/cloud-connectivity",
        Icon: ConnectivityKontrolSVG,
      },
    ],
  },
  {
    label: "L3",
    id: "kontrol-layer-l3",
    children: [
      {
        label: "Internet connectivity",
        id: "kontrol-ipx",
        path: "/kontrol/ipx",
        Icon: InternetSVG,
      },
      {
        label: "Backbone",
        id: "kontrol-backbone",
        path: "/kontrol/backbone",
        Icon: BackboneSVG,
      },
    ],
  },
  {
    // Managed services rather than a layer: both run on top of L1–L3 instead of
    // being one of them, which is why they get a section of their own.
    label: "Security & SD-WAN",
    id: "kontrol-security-sdwan",
    children: [
      {
        label: "SD-WAN",
        id: "sdwan-hub-root",
        path: "/kontrol/sdwan-hub",
        Icon: IconSdwan,
      },
      {
        label: "Virtual firewall",
        id: "fwaas-root",
        path: "/kontrol/fwaas",
        Icon: IconFirewall,
      },
    ],
  },
  // Aquí quitamos el "Management" de dentro de CTX Reflector.
  // Dejamos "L3 Overview" como hijo directo.
  {
    label: "CTX Reflector",
    id: "ctx-reflector",
    Icon: IconReflector,
    children: [
      {
        label: "L3 Overview",
        id: "reflector-l3overview",
        children: [
          {
            label: "BGP Overview",
            id: "reflector-l3overview-bgp",
            path: "/reflector/l3overview/bgp",
          },
          {
            label: "Logs Detail",
            id: "reflector-l3overview-logs",
            path: "/reflector/l3overview/logs",
          },
        ],
      },
      // <-- Ya NO incluimos "Management" aquí
    ],
  },
];

// NUEVO: sección "Management" top-level
export const managementItems: MenuItem[] = [
  {
    label: "Licensing",
    id: "reflector-management-licensing",
    path: "/reflector/management/licensing",
  },
  {
    label: "Instances",
    id: "reflector-management-instances",
    children: [
      {
        label: "Licensed",
        id: "reflector-instances-licensed",
        path: "/reflector/management/instances/licensed",
      },
      {
        label: "Unlicensed",
        id: "reflector-instances-unlicensed",
        path: "/reflector/management/instances/unlicensed",
      },
      {
        label: "High Availability",
        id: "reflector-instances-highavailability",
        path: "/reflector/management/instances/highavailability",
      },
      {
        label: "Health Check",
        id: "reflector-instances-healthcheck",
        path: "/reflector/management/instances/healthcheck",
      },
      {
        label: "Syslog",
        id: "reflector-instances-syslog",
        path: "/reflector/management/instances/syslog",
      },
    ],
  },
  {
    label: "BGP",
    id: "reflector-management-bgp",
    children: [
      {
        label: "Neighbors",
        id: "reflector-bgp-management",
        path: "/reflector/management/bgp/neighbors",
      },
      {
        label: "Status",
        id: "reflector-bgp-status",
        path: "/reflector/management/bgp/status",
      },
    ],
  },
  {
    label: "Syslog",
    id: "reflector-management-reporting",
    path: "/reflector/reporting",
  },
];


// === Account area (shared across all products) ===

/**
 * The one row that is about the **person signed in**, not about the tenant.
 *
 * It does not render in the rail: it lives in the header's account panel, under
 * the avatar that already shows who you are — which is where a reader looks for
 * their own account, and the only thing there that is theirs rather than the
 * tenant's. Exported from here anyway so the panel takes its label, path and
 * icon from the same place every other row does, and so it stays in
 * `accountAreaItems` for the reachability check.
 */
export const profileAreaItem: MenuItem = {
  label: "Profile",
  id: "account-management-profile",
  path: "/account/profile",
  Icon: ProfileCircle,
};

/**
 * Settings that are about the **tenant**: who belongs to it, what it is
 * notified about, how its resources are labelled, what it did.
 *
 * Order keeps the rows every tenant type sees ahead of the GLOBAL_ADMIN-only
 * ones, so a customer's group ends where their access does instead of opening
 * with gaps.
 */
export const organizationAreaItems: MenuItem[] = [
  {
    label: "Notifications",
    id: "account-management-notifications",
    path: "/account/notifications",
    Icon: IconNotifications,
    badgeBeta: "Beta",
  },
  {
    label: "Tags",
    id: "account-management-tags",
    path: "/account/tags-manager",
    Icon: IconTag,
    badgeBeta: "Beta",
  },
  {
    label: "Users",
    id: "account-management-users",
    path: "/account/users",
    Icon: IconProfile2User,
  },
  {
    label: "Customers",
    id: "account-management-customers",
    path: "/account/customers",
    Icon: IconPeople,
  },
  {
    // Audit Logs sits with the account rows, not under Support: it reads as
    // "my tenant's activity", and its route living under `/support` is an
    // accident of where the page was first mounted.
    label: "Audit Logs",
    id: "support-audit-logs",
    path: "/support/audit-logs",
    Icon: LogsSVG,
    badgeBeta: "Beta",
  },
];

/**
 * API is neither personal nor organizational chrome — it is a surface of the
 * product, and the one destination here a caller comes looking for by name.
 *
 * It does not render in the rail either: it is a button of its own in the
 * header, beside the avatar (`HeaderApiControl`), so it can be reached without
 * opening a menu first. Every arrangement that filed it under something else
 * buried it. Exported from here so that control takes its label, path and icon
 * from the same place every other row does, and so it stays in
 * `accountAreaItems` for the visibility filter and the reachability check.
 */
export const apiAreaItem: MenuItem = {
  // The `id` stays `…-integrations` on purpose: the prod/BETA gate keys off
  // it, and renaming it would silently unhide the row in production (CTX-37).
  label: "API",
  id: "account-management-integrations",
  path: "/account/api",
  Icon: IconKey,
  // No `badgeBeta`: the header control wears its label, and a pill hung off a
  // header button reads as clutter rather than as a caveat. Nothing is claimed
  // by dropping it — `isCustomerApiEnabled` is what actually keeps the page out
  // of production.
};

/**
 * How the account area renders in the rail, appended to every product's nav by
 * `SideMenuNavByProduct` (CTX-512, completed in CTX-581).
 *
 * What is left after the two destinations that are not the tenant's settings
 * moved to the header: Profile, which is personal and sits in the account panel
 * beside the identity it edits (`profileAreaItem`), and API, which is a button
 * of its own beside the avatar (`apiAreaItem`). An entry with `children`
 * renders as a titled group; one without renders as a plain row — the shape the
 * services area already uses, so a future lone row needs no special case.
 *
 * Per-item visibility is decided in exactly one place, `filterAccountAreaItems`
 * in `sideMenuVisibility.ts`, keyed by these ids. Ids are load-bearing:
 * renaming one silently unhides its row. Every row carries an `Icon` because
 * the collapsed rail renders the glyph alone.
 */
export const accountAreaSections: MenuItem[] = [
  {
    label: "Organization",
    id: "account-area-organization",
    children: organizationAreaItems,
  },
];

/**
 * Every account row, flat — the rail's and the header's alike.
 *
 * Derived rather than hand-maintained: a row added above reaches the visibility
 * filter and the reachability check without a second edit, so the two can never
 * disagree about which rows exist. Profile and API are included even though
 * they render in the header, because their routes are reachable and this is
 * what says so (`pathReachability.ts`) — and because API's audience is decided
 * by the same filter wherever it renders.
 */
export const accountAreaItems: MenuItem[] = [
  profileAreaItem,
  ...organizationAreaItems,
  apiAreaItem,
];

/** Help — support entry points. */
export const helpItems: MenuItem[] = [
  {
    label: "Technical Support",
    id: "request-root",
    path: "/support/request",
    Icon: SupportSVG,
  },
];

// === CTX Reflector V2 Menu Items ===
export const reflectorV2MenuItems: MenuItem[] = [
  { label: "Overview", id: "reflector-v2-overview", path: "/reflector-v2/overview", Icon: HomeSvg },
  {
    label: "Management",
    id: "reflector-v2-management",
    children: [
      { label: "Reflectors", id: "reflector-v2-bgp-reflectors", path: "/reflector-v2/management/bgp-reflectors" , Icon: ServerSVG},
      { label: "BGP Visibility", id: "reflector-v2-bgp-visibility", path: "/reflector-v2/management/bgp-visibility", Icon: IconEyeSmall },
      // CTX-97 — the source-first Network Topology product launches inside
      // Reflector V2 (moved from the Insight menu, 2026-08-04); a plain
      // Management entry, no section of its own. GA in every environment
      // since 2026-08-06 — `isInsightTopologyV2Enabled()` is kept as the
      // single switch in case it ever needs turning off in a hurry.
      ...(isInsightTopologyV2Enabled()
        ? [
            {
              label: "Network Topology",
              id: "reflector-v2-network-topology",
              path: "/reflector-v2/network-topology",
              Icon: ConnectivityKontrolSVG,
            },
          ]
        : []),
      { label: "Licensing", id: "reflector-v2-licensing", path: "/reflector-v2/management/licensing" , Icon: IconLicense},
      { label: "Syslog", id: "reflector-v2-reporting", path: "/reflector-v2/reporting", Icon: ReportSVG },
      { label: "Logs", id: "reflector-v2-logs", path: "/reflector-v2/logs", Icon: LogsSVG },
    ],
  },
  // Settings-area sections (Customers / Administration / Personal Settings /
  // Account / Help) are appended for every product by SideMenuNavByProduct,
  // so they are no longer embedded here.
];

// === CTX Insight Menu Items ===
export const insightMenuItems: MenuItem[] = [
  {
    label: "Main",
    id: "insight-main",
    children: [
      { label: "Home", id: "insight-home", path: "/insight/home", Icon: HomeSvg },
      { label: "Network Topology", id: "insight-network-topology", path: "/insight/topology", Icon: ConnectivityKontrolSVG },
      { label: "Optimization Hub", id: "insight-optimization", path: "/insight/optimization", Icon: Quotation },
    ],
  },
  // Settings-area sections are appended for every product by
  // SideMenuNavByProduct, so they are no longer embedded here.
];
